import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import AdminLayout from '../../components/admin/AdminLayout';
import Avatar from '../../components/common/Avatar';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const UserTagsManager = () => {
  const [users, setUsers] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userTags, setUserTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, tagsRes] = await Promise.all([
        api.get('/auth/users'),
        api.get('/profile-tags')
      ]);

      if (usersRes.data.success) {
        setUsers(usersRes.data.users);
      }
      if (tagsRes.data.success) {
        setTags(tagsRes.data.tags);
      }
    } catch (error) {
      console.error('Erreur chargement:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadUserTags = async (userId) => {
    try {
      const response = await api.get(`/profile-tags/user/${userId}`);
      if (response.data.success) {
        setUserTags(response.data.tags.map(t => t.id));
      }
    } catch (error) {
      console.error('Erreur chargement tags utilisateur:', error);
      setUserTags([]);
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    loadUserTags(user.id);
  };

  const handleTagToggle = (tagId) => {
    if (userTags.includes(tagId)) {
      setUserTags(userTags.filter(id => id !== tagId));
    } else {
      setUserTags([...userTags, tagId]);
    }
  };

  const handleSave = async () => {
    if (!selectedUser) return;

    try {
      setSaving(true);
      await api.post('/profile-tags/assign', {
        userId: selectedUser.id,
        tagIds: userTags
      });

      toast.success('Tags mis à jour avec succès');
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <Helmet>
        <title>Assigner Tags - Admin SunServ</title>
      </Helmet>

      <div className="p-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-2">Assigner des Tags aux Utilisateurs</h1>
          <p className="text-gray-400">
            Sélectionnez un utilisateur et assignez-lui des tags de profil
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-12 h-12 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-400 mt-4">Chargement...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Liste des utilisateurs */}
            <div className="bg-surface rounded-xl p-6 border border-gray-700/30">
              <h2 className="text-xl font-bold text-white mb-4">
                <i className="fas fa-users mr-2 text-yellow-400"></i>
                Utilisateurs
              </h2>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {users.map(user => (
                  <button
                    key={user.id}
                    onClick={() => handleUserSelect(user)}
                    className={`w-full flex items-center gap-4 p-4 rounded-lg transition-all text-left ${
                      selectedUser?.id === user.id
                        ? 'bg-yellow-400/20 border-2 border-yellow-400'
                        : 'bg-gray-800/30 border-2 border-transparent hover:border-gray-600'
                    }`}
                  >
                    <Avatar 
                      user={{ username: user.username, avatar: user.avatar }}
                      size="md"
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-white">
                        {user.display_name || user.username}
                      </p>
                      <p className="text-sm text-gray-400">@{user.username}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs ${
                      user.role === 'admin' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {user.role === 'admin' ? 'Admin' : user.role === 'redacteur' ? 'Rédacteur' : 'Contributeur'}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Tags disponibles */}
            <div className="bg-surface rounded-xl p-6 border border-gray-700/30">
              <h2 className="text-xl font-bold text-white mb-4">
                <i className="fas fa-tags mr-2 text-yellow-400"></i>
                Tags {selectedUser && `pour ${selectedUser.display_name || selectedUser.username}`}
              </h2>

              {selectedUser ? (
                <>
                  <div className="space-y-3 mb-6 max-h-[500px] overflow-y-auto">
                    {tags.map(tag => (
                      <label
                        key={tag.id}
                        className="flex items-center gap-4 p-4 rounded-lg bg-gray-800/30 hover:bg-gray-800/50 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={userTags.includes(tag.id)}
                          onChange={() => handleTagToggle(tag.id)}
                          className="w-5 h-5 rounded border-gray-600 text-yellow-400 focus:ring-yellow-400"
                        />
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: tag.color }}
                        >
                          <i className="fas fa-tag text-gray-900"></i>
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-white">{tag.name}</p>
                          <p className="text-sm text-gray-400">{tag.color}</p>
                        </div>
                      </label>
                    ))}
                  </div>

                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="w-full bg-yellow-400 hover:bg-yellow-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-gray-900 px-6 py-3 rounded-xl font-semibold transition-all"
                  >
                    {saving ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save mr-2"></i>
                        Enregistrer les tags
                      </>
                    )}
                  </button>
                </>
              ) : (
                <div className="text-center py-12 text-gray-400">
                  <i className="fas fa-hand-pointer text-5xl mb-4"></i>
                  <p>Sélectionnez un utilisateur pour assigner des tags</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default UserTagsManager;
